import { cloneDeep, isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Path, PathValue, UseFormReturn } from 'react-hook-form';

type Updater<T, F extends Path<T>> = (
  value: PathValue<T, F>,
  prevValue: PathValue<T, F>,
  context: Partial<T>
) => Partial<T>;

export type FormUpdateConfig<T> = { [K in Path<T>]?: Updater<T, K> };
/**
 * Use this hook to run some update logic whenever some of the form values
 * change. Like if you need to keep some form values in sync and update them
 * whenever the related field is changed.
 *
 * @param config Object that maps field name to updater functions.
 * The object should be of shape `{ 'form-field-name': updaterFunciton }`.
 *
 * If you return an object from the updater function, it will be used to update
 * form values.
 * If you wan't to run you update logic manually, you can return `null` to
 * disable the default behavior.
 * @param form The object returned from the `useForm` hook.
 * @param deps Here you should provide a list of dependencies that cause the
 * form value to change. This list will be used in `useEffect` to ignore the
 * next form value change. This way your updater functions don't run as a result
 * of resetting form value after, for example, submitting a form.
 */
export function useFormUpdate<T>(
  config: FormUpdateConfig<T>,
  form: UseFormReturn<T>
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type TValues = any;

  const [initialConfig] = useState(config);

  const savedValuesRef = useRef<TValues>(null);

  useEffect(() => {
    const set = new Set<string>();
    const update = (infoName: string) => {
      const savedValues = cloneDeep(savedValuesRef.current);
      const newValues: TValues = {};

      // Get all new form values.
      Object.keys(initialConfig).forEach((name: Path<T>) => {
        const value = form.getValues(name);
        newValues[name] = value;
      });

      const updater = initialConfig[infoName];

      if (!updater) {
        // Update saved form values for fields without updaters
        savedValuesRef.current = {
          ...savedValuesRef.current,
          [infoName]: newValues[infoName],
        };
        return;
      }

      const savedValue = savedValues?.[infoName];
      const newValue = newValues[infoName];

      if (isEqual(savedValue, newValue)) return;

      const changes = updater(newValue, savedValue, form.getValues());

      if (changes != null) {
        // Apply only changes that are actually different.
        Object.entries(changes).forEach(([name, value]: [Path<T>, any]) => {
          const currentValue = form.getValues(name);

          // Only setValue if the value has actually changed
          if (!isEqual(currentValue, value)) {
            // Only mark as dirty if field is not already dirty
            // @ts-expect-error PathValue<T, F>
            const shouldTrigger = form.formState.dirtyFields[name]
              ? undefined
              : true;
            form.setValue(name, value, {
              shouldDirty: shouldTrigger,
              shouldValidate: shouldTrigger,
            });
          }
        });
      }

      // Update saved form values AFTER running the updater
      savedValuesRef.current = {
        ...savedValuesRef.current,
        [infoName]: newValue,
      };
    };

    let timeout = null;
    const subscription = form.watch((_value, info) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        set.forEach((name) => {
          update(name);
        });
        set.clear();
      }, 0);

      // Updates caused by calling `form.reset` will have an empty `name`
      // property. These should be skipped.
      if (info.name === undefined) return;
      set.add(info.name);

      return () => {
        subscription.unsubscribe();
      };
    });
  }, [initialConfig, form]);
}
